import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Images } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import Text from "../../../../components/typography/Text";
import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";

import AlbumThumbnailCard from "../../../../components/cards/thumbnails/albums/AlbumThumbnailCard";

import { useHomeAlbums } from "../hooks/useHomeAlbums";

import "./HomeAlbumsView.css";

const LIMIT = 5;

export default function HomeAlbumsView() {
  const navigate = useNavigate();

  const { albums, loading, error, refresh } = useHomeAlbums({
    limit: LIMIT,
  });

  const albumItems = useMemo(() => {
    const seen = new Set();

    return (Array.isArray(albums) ? albums : [])
      .filter((album) => {
        const key = album?.albumId ?? album?.albumTag;

        if (key == null || key === "") return false;

        const identity = String(key);

        if (seen.has(identity)) return false;

        seen.add(identity);
        return true;
      })
      .slice(0, LIMIT);
  }, [albums]);

  const hasAlbums = albumItems.length > 0;

  const handleOpenAlbum = (album) => {
    const albumTag = String(album?.albumTag || "").trim();

    if (!albumTag) return;

    navigate(`/albums/${encodeURIComponent(albumTag)}`);
  };

  const renderAlbum = (album) => (
    <AlbumThumbnailCard
      key={album.albumId ?? album.albumTag}
      album={album}
      onClick={() => handleOpenAlbum(album)}
    />
  );

  return (
    <ViewContainer>
      <ViewHeader
        title="Albums"
        actions={
          <Link to="/katchrugby?tab=albums" className="home-albums__view-all">
            View all
          </Link>
        }
      />

      {loading && !hasAlbums ? (
        <ContentGrid
          items={[]}
          loading
          columns={4}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          skeletonCount={LIMIT}
        />
      ) : !hasAlbums ? (
        <EmptyState
          icon={<Images size={28} />}
          title={error ? "Unable to load albums" : "No albums available"}
          description={
            error || "There are no public photo albums available right now."
          }
          action={
            error ? (
              <Button
                value="Try Again"
                variant="primary-outlined"
                onClick={refresh}
              />
            ) : null
          }
        />
      ) : (
        <>
          {error && (
            <div className="home-albums__error" role="alert">
              <Text as="p" size="sm">
                {error}
              </Text>
            </div>
          )}

          <ContentGrid
            items={albumItems}
            columns={4}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            showEmpty={false}
            renderItem={renderAlbum}
          />
        </>
      )}
    </ViewContainer>
  );
}
